import { useMemo } from 'react';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Ticket, Settings, Puzzle } from 'lucide-react';
import type { SchoolOptions, OfferModuleOption } from '@/types/school';
import type { V2TenantGroup } from '@/types/v2-deployment';

export interface V2SubscriptionFormData {
  tenantGroupId?: string;
  offerId: number | null;
  optionIds: number[];
  moduleIds: number[];
}

interface V2SubscriptionFormProps {
  options: SchoolOptions | null;
  tenantGroups?: V2TenantGroup[];
  defaultTenantGroupId?: string;
  value: V2SubscriptionFormData;
  onChange: (data: V2SubscriptionFormData) => void;
  showTenantGroup?: boolean;
  compact?: boolean;
}

/** Prix HT d'un module par tranche, ex : "5,40 / 4,62 / 3,24 / 2,70 € HT" */
export function formatModulePrices(module: OfferModuleOption): string | null {
  const prices = [module.price, module.priceT2, module.priceT3, module.priceT4];
  if (prices.every((p) => p === null || p === undefined)) return null;
  if (prices.every((p) => !p)) return 'Offert';
  return (
    prices
      .map((p) => (p === null || p === undefined ? '—' : p.toLocaleString('fr-FR', { minimumFractionDigits: 2 })))
      .join(' / ') + ' € HT'
  );
}

export function V2SubscriptionForm({
  options,
  tenantGroups,
  defaultTenantGroupId,
  value,
  onChange,
  showTenantGroup = true,
  compact = false,
}: V2SubscriptionFormProps) {
  // Get selected offer
  const selectedOffer = useMemo(() => {
    return options?.offers.find((o) => o.id === value.offerId);
  }, [options?.offers, value.offerId]);

  const selectedProductCode = selectedOffer?.productCode || null;

  // Get options for selected product
  const optionsForProduct = useMemo(() => {
    if (!selectedProductCode || !options?.productOptions) return [];
    return options.productOptions.filter((o) => o.productCode === selectedProductCode);
  }, [options?.productOptions, selectedProductCode]);

  // Modules of the selected offer: included by default vs optional (EXPERT "sur mesure")
  const defaultModules = useMemo(
    () => selectedOffer?.modules.filter((m) => m.isDefault) || [],
    [selectedOffer]
  );
  const optionalModules = useMemo(
    () => selectedOffer?.modules.filter((m) => !m.isDefault) || [],
    [selectedOffer]
  );

  const handleOptionToggle = (optionId: number) => {
    const currentOptions = value.optionIds || [];
    const newOptions = currentOptions.includes(optionId)
      ? currentOptions.filter((id) => id !== optionId)
      : [...currentOptions, optionId];
    onChange({ ...value, optionIds: newOptions });
  };

  const handleModuleToggle = (moduleId: number) => {
    const currentModules = value.moduleIds || [];
    const newModules = currentModules.includes(moduleId)
      ? currentModules.filter((id) => id !== moduleId)
      : [...currentModules, moduleId];
    onChange({ ...value, moduleIds: newModules });
  };

  const selectedGroup = tenantGroups?.find((g) => g.id === value.tenantGroupId);
  const isDefaultGroup = value.tenantGroupId === defaultTenantGroupId;

  return (
    <div className={`space-y-4 ${compact ? '' : ''}`}>
      {/* Tenant Group Selection */}
      {showTenantGroup && tenantGroups && tenantGroups.length > 0 && (
        <div className="space-y-2">
          <Label>Reseau V2 (Tenant Group)</Label>
          <Select
            value={value.tenantGroupId || defaultTenantGroupId || ''}
            onValueChange={(val) => onChange({ ...value, tenantGroupId: val })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Selectionner un reseau" />
            </SelectTrigger>
            <SelectContent>
              {tenantGroups.map((group) => (
                <SelectItem key={group.id} value={group.id}>
                  {group.name}
                  {group.id === defaultTenantGroupId ? ' (Par defaut)' : ''}
                  {group.tenantCount > 0 ? ` - ${group.tenantCount} écoles` : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedGroup && (
            <p className="text-xs text-muted-foreground">
              Domaine: {selectedGroup.host || 'Non defini'}
            </p>
          )}
          {/* Info box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-800">
              {isDefaultGroup || !selectedGroup ? (
                <>
                  L'école sera créée dans le réseau <strong>École Futée</strong> par défaut.
                </>
              ) : (
                <>
                  L'école sera créée dans le réseau <strong>{selectedGroup.name}</strong>.
                </>
              )}
            </p>
          </div>
        </div>
      )}

      {/* Offer Selection */}
      <div className="space-y-2">
        <Label className="flex items-center gap-2">
          <Ticket className="h-4 w-4" />
          Offre
        </Label>
        <Select
          value={value.offerId?.toString() || 'none'}
          onValueChange={(val) => {
            const offerId = val === 'none' ? null : parseInt(val);
            onChange({
              ...value,
              offerId,
              optionIds: [], // Reset options when offer changes
              moduleIds: [], // Reset extra modules when offer changes
            });
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder="Selectionner une offre" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Selectionner une offre</SelectItem>
            {options?.offers.map((offer) => (
              <SelectItem key={offer.id} value={offer.id.toString()}>
                {offer.productName} - {offer.name} ({offer.moduleCount} modules)
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Options - only show if offer is selected */}
      {selectedOffer && optionsForProduct.length > 0 && (
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <Settings className="h-4 w-4" />
            Options
          </Label>
          <div className="grid grid-cols-1 gap-2 border rounded-md p-2">
            {optionsForProduct.map((option) => (
              <div key={option.id} className="flex items-center space-x-2">
                <Checkbox
                  id={`v2-option-${option.id}`}
                  checked={value.optionIds.includes(option.id)}
                  onCheckedChange={() => handleOptionToggle(option.id)}
                />
                <Label
                  htmlFor={`v2-option-${option.id}`}
                  className="text-sm font-normal cursor-pointer flex items-center gap-2"
                >
                  {option.name}
                  <Badge variant="outline" className="text-xs">
                    {option.type === 'recurring' ? 'Recurrent' : 'One-shot'}
                  </Badge>
                </Label>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Default modules - read-only display based on selected offer */}
      {selectedOffer && defaultModules.length > 0 && (
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <Puzzle className="h-4 w-4" />
            Modules inclus ({defaultModules.length})
          </Label>
          <div className="flex flex-wrap gap-2 p-2 border rounded-md bg-muted/20">
            {defaultModules.map((module) => (
              <Badge key={module.id} variant="secondary">
                {module.name}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Optional modules - selectable per school (EXPERT "sur mesure") */}
      {selectedOffer && optionalModules.length > 0 && (
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <Puzzle className="h-4 w-4" />
            Modules complémentaires au choix
          </Label>
          <div className="grid grid-cols-1 gap-2 border rounded-md p-2">
            {optionalModules.map((module) => {
              const prices = formatModulePrices(module);
              return (
                <div key={module.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={`v2-module-${module.id}`}
                    checked={value.moduleIds.includes(module.id)}
                    onCheckedChange={() => handleModuleToggle(module.id)}
                  />
                  <Label
                    htmlFor={`v2-module-${module.id}`}
                    className="text-sm font-normal cursor-pointer flex items-center gap-2"
                  >
                    {module.name}
                    {prices && (
                      <span className="text-xs text-muted-foreground">{prices}</span>
                    )}
                  </Label>
                </div>
              );
            })}
          </div>
          <p className="text-xs text-muted-foreground">
            Prix HT par élève et par an, selon la tranche d'effectif (0-100 / 101-250 / 251-500 / 501+).
          </p>
        </div>
      )}
    </div>
  );
}
